import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { MY_FORMATS_MONTH } from '@app/_common/dateFormats';
import {
  AuthenticationService,
  EventService,
  EventType,
  HelperService,
  ObjectType,
  LoanPaymentService,
  LoanService,
} from '@app/_services';
import moment from 'moment';
import { Moment } from 'moment';
import { Subject } from 'rxjs';
import * as _ from 'lodash';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import {
  LoanPaymentModel,
  LoanModel,
  LoanPaymentType,
} from '@app/_common/models/loan';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LoanWidgetComponent } from '../loan-widget/loan-widget.component';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LoanPaymentWidgetComponent } from '../loan-payment-widget/loan-payment-widget.component';
import { DeleteDialogComponent } from '@app/_shared/delete-dialog/delete-dialog.component';

@Component({
  selector: 'app-loan',
  templateUrl: './loan.component.html',
  styleUrls: ['./loan.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS_MONTH },
    { provide: MAT_DATE_LOCALE, useValue: 'he' },
  ],
})
export class LoanComponent implements OnInit, OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();

  public date: UntypedFormControl;
  public selectedMonth: string = '';

  public groups: any = [];
  private payments: LoanPaymentModel[] = [];

  public isLoading: boolean = false;
  public isLoadingTable: boolean = false;

  public loanId: number;
  public loan: LoanModel;

  public total: number = 0;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private paymentService: LoanPaymentService,
    private loanService: LoanService,
    private eventService: EventService,
    private authenticationService: AuthenticationService,
    private helperService: HelperService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.loanId = +params['id'];
      this.eventService.send({
        type: EventType.Select,
        objectType: ObjectType.Loan,
        object: { loanId: this.loanId },
      });
    });

    this.eventService
      .getUpdate()
      .pipe(
        filter(e => e.objectType === ObjectType.LoanPayment),
        takeUntil(this.destroy$)
      )
      .subscribe(event => {
        if (
          ((event.type === EventType.Create ||
            event.type === EventType.Update) &&
            event.object.loanId === this,
          this.loanId)
        ) {
          this.load();
        }
      });

    this.setStartDate();
    this.updateDateRange();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setStartDate(): void {
    const selectedDate = localStorage.getItem('selectedDate');

    let monthDate;
    if (selectedDate) {
      const obj = JSON.parse(selectedDate);
      if (moment.utc() < moment(obj.expired)) {
        monthDate = moment(obj.date, null, 'he');
      }
    }

    if (!monthDate || !monthDate.isValid()) {
      const date = this.helperService.getMonthStartDate();
      monthDate = moment(
        new Date(
          date.year(),
          date.month(),
          this.authenticationService.currentUserValue.user.startMonthDay
        ),
        null,
        'he'
      );
    }
    this.date = new UntypedFormControl(monthDate);
  }

  updateDateRange(): void {
    const monthDate = moment(
      new Date(
        this.date.value.year(),
        this.date.value.month(),
        this.authenticationService.currentUserValue.user.startMonthDay
      ),
      null,
      'he'
    );
    this.selectedMonth = monthDate.format('YYYY MMM');

    this.load();
  }

  load(): void {
    if (!this.loanId) {
      return;
    }

    const date = this.date.value;
    this.groups = [];
    this.payments = [];

    this.isLoading = true;
    this.loanService
      .list(this.loanId)
      .pipe(
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        if (data && data.length) {
          this.loan = data[0];
        }
      });

    this.loadPayments(date);
  }

  loadPayments(date: any): void {
    this.isLoadingTable = true;

    this.paymentService
      .list(0, null, this.loanId, date.year(), date.month() + 1)
      .pipe(
        finalize(() => (this.isLoadingTable = false)),
        takeUntil(this.destroy$)
      )
      .subscribe(data => {
        this.payments = data;

        const groups = _.groupBy(this.payments, x => {
          //console.info(x.dateTime);
          const date = moment.utc(x.dateTime, null, 'he').local();
          return date.format('dddd, D MMMM YYYY');
        });

        const result = [];
        _.forEach(groups, function (value, key) {
          result.push({
            date: key,
            payments: _.sortBy(value, x => moment.utc(x.dateTime)),
          });
        });

        this.groups = result;
      });
  }

  chosenYearHandler(normalizedYear: Moment) {
    const ctrlValue = this.date.value;
    ctrlValue.year(normalizedYear.year());
    this.date.setValue(ctrlValue);
  }

  chosenMonthHandler(
    normalizedMonth: Moment,
    datepicker: MatDatepicker<Moment>
  ) {
    this.date.setValue(normalizedMonth);
    datepicker.close();

    localStorage.setItem(
      'selectedDate',
      JSON.stringify({
        date: this.date.value,
        expired: moment.utc().add(1, 'day'),
      })
    );
    this.updateDateRange();
  }

  editItem(item: any): void {
    const dialogRef: MatDialogRef<LoanPaymentWidgetComponent, any> =
      this.dialog.open(LoanPaymentWidgetComponent, {
        data: {
          ...item,
        },
        panelClass: 'main-widget-panel',
      });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.eventService.send({
            type: EventType.Update,
            objectType: ObjectType.LoanPayment,
            object: result,
          });
          this.load();
        }
      });
  }

  deleteItem(item: any): void {
    this.dialog
      .open(DeleteDialogComponent, {
        panelClass: 'main-widget-panel',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result?.value) {
          const date = moment(item.dateTime);
          this.paymentService
            .delete(item.id, date.year(), date.month() + 1)
            .pipe(takeUntil(this.destroy$))
            .subscribe(data => {
              this.eventService.send({
                type: EventType.Delete,
                objectType: ObjectType.LoanPayment,
                object: item,
              });
              this.load();
            });
        }
      });
  }

  editLoan(): void {
    const dialogRef: MatDialogRef<LoanWidgetComponent, any> = this.dialog.open(
      LoanWidgetComponent,
      {
        data: {
          ...this.loan,
        },
        panelClass: 'main-widget-panel',
      }
    );

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (result) {
          this.eventService.send({
            type: EventType.Update,
            objectType: ObjectType.Loan,
            object: { ...result },
          });
          this.load();
        }
      });
  }

  deleteLoan(): void {
    this.dialog
      .open(DeleteDialogComponent, {
        panelClass: 'main-widget-panel',
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        result => {
          if (result?.value) {
            this.loanService
              .delete(this.loan.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe(data => {
                this.eventService.send({
                  type: EventType.Delete,
                  objectType: ObjectType.Loan,
                  object: this.loan,
                });
                this.router.navigate(['loans']);
              });
          }
        },
        err => this.toastr.error('Error')
      );
  }
}
